// libraries
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { selectors as offerReduxSelectors } from '@makemydeal/dr-offer-redux';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import {
    DEAL_STRUCTURE_CONTEXT_LIST_CAPS,
    DEAL_STRUCTURE_CONTEXT_SCHEMA_VERSION,
    DEAL_STRUCTURE_CONTEXT_STRING_CAPS
} from '@makemydeal/dr-dash-bff-types';
import type {
    DealStructureContext,
    DealStructureContextDealType,
    DealStructureContextItem,
    DealStructureContextRate,
    DealStructureContextTradeIn
} from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import { getPrimaryScopeState } from '../selectors/primaryScopeSelectors';
import {
    getBuyRateOverrideWithFallback,
    getFlatOrItemizedTaxes,
    getItemizedDealerFees,
    getItemizedGovernmentFees,
    getRateTypeOverrideFromOfferTypeOrSelectedTerm
} from '../selectors/offerSelectors';
import { getCountedLenderFees } from '../selectors/compositeSelectors';
import { getVppSelectedProducts, getVppSummary } from '../selectors/vehicleProtectionSelectors';
import { getIncludedTradeIns, getTradeInSummary } from '../selectors/tradeIns';
import { getRetainedProgramDisplay } from '../selectors/programsSelectors';
import { getSummaryDiscount } from '../selectors/dealerDiscountsSelectors';

type FinancialDetails = DealStructureContext['financialDetails'];
type ItemList = { itemCount: number; items: DealStructureContextItem[] };

const DEAL_TYPE_BY_OFFER_TYPE: Record<string, DealStructureContextDealType> = {
    [FINANCE]: 'Finance',
    [LEASE]: 'Lease',
    [CASH]: 'Cash',
    [BALLOON]: 'Balloon'
};

const INVENTORY_TYPE_BY_CONDITION: Record<string, string> = {
    new: 'New',
    used: 'Used',
    certified: 'CertifiedPreOwned'
};

const MONEY_FACTOR_RATE_TYPE = 'MoneyFactor';

// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g;
const STRIPPED_CHARACTERS =
    /([\ud800-\udb3f\udb41-\udbff][\udc00-\udfff])|[\ud800-\udfff<>\u00ad\u061c\u180e\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff\ufff9-\ufffb\ufe64\ufe65\uff1c\uff1e]/g;

export const toSafeText = (value: unknown, maxLength: number): string => {
    if (value === undefined || value === null) return '';
    const safeText = String(value)
        .replace(UNSAFE_CHARACTERS, ' ')
        .replace(STRIPPED_CHARACTERS, (match, pair) => pair || '')
        .trim()
        .slice(0, maxLength);
    return safeText.replace(/[\ud800-\udbff]$/, '').trim();
};

export const toFiniteNumber = (value: unknown): number | undefined => {
    const candidate = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : undefined;
};

const toNonZeroNumber = (value: unknown): number | undefined => {
    const amount = toFiniteNumber(value);
    return amount ? amount : undefined;
};

const toItemList = <T>(source: T[], cap: number, toItem: (entry: T) => DealStructureContextItem | undefined): ItemList => {
    const items = source.map(toItem).filter((item): item is DealStructureContextItem => item !== undefined);
    return { itemCount: items.length, items: items.slice(0, cap) };
};

const toItem = (name: unknown, amount: unknown): DealStructureContextItem | undefined => {
    const finiteAmount = toFiniteNumber(amount);
    if (finiteAmount === undefined) return undefined;
    return { name: toSafeText(name, DEAL_STRUCTURE_CONTEXT_STRING_CAPS.itemName), amount: finiteAmount };
};

const getDealType = (primary: StateTree, offerType: OfferType): DealStructureContextDealType | undefined => {
    if (offerType !== CASH && offerReduxSelectors.isBalloonDeal(primary)) return 'Balloon';
    return DEAL_TYPE_BY_OFFER_TYPE[offerType];
};

const buildSellingVehicle = (primary: StateTree): DealStructureContext['vehicles']['sellingVehicle'] | undefined => {
    const vehicle = offerReduxSelectors.getVehicleDetails(primary);
    if (!vehicle.make && !vehicle.model && !vehicle.trim) return undefined;

    const { text, vin } = DEAL_STRUCTURE_CONTEXT_STRING_CAPS;
    const condition = toSafeText(vehicle.condition, text);
    const exteriorColor = toSafeText(vehicle.exteriorColor, text);
    return {
        vin: toSafeText(vehicle.vin, vin),
        stockNumber: toSafeText(vehicle.stockNumber, text),
        year: vehicle.year ? toSafeText(vehicle.year, text) : '',
        make: toSafeText(vehicle.make, text),
        model: toSafeText(vehicle.model, text),
        trim: toSafeText(vehicle.trim, text),
        inventoryType: INVENTORY_TYPE_BY_CONDITION[condition.toLowerCase()] || condition,
        odometerMileage: Math.max(toFiniteNumber(vehicle.mileage) ?? 0, 0),
        ...(exteriorColor && { exteriorColor })
    };
};

const buildPaymentDetails = (
    primary: StateTree,
    offerType: OfferType,
    dealType: DealStructureContextDealType
): FinancialDetails['paymentDetails'] | undefined => {
    if (offerReduxSelectors.isSelectedTermRecordMissing(primary, offerType)) return undefined;

    const term = toFiniteNumber(offerReduxSelectors.getSelectedTermMonths(primary, offerType));
    const payment = toFiniteNumber(offerReduxSelectors.getMonthlyPayment(primary, offerType));
    if (!term || term < 0 || !Number.isInteger(term) || payment === undefined) return undefined;

    const amountFinanced =
        dealType === 'Lease' ? undefined : toFiniteNumber(offerReduxSelectors.getSelectedTermAmountFinanced(primary, offerType));
    const totalOfPayments = dealerSelectors.isCarsActDisclosuresApplicable(primary)
        ? toFiniteNumber(offerReduxSelectors.getTotalOfPaymentsAndConsideration(primary, offerType))
        : undefined;
    const paymentDetails: NonNullable<FinancialDetails['paymentDetails']> = {
        term,
        payment,
        ...(amountFinanced !== undefined && { amountFinanced }),
        ...(totalOfPayments !== undefined && { totalOfPayments }),
        paymentFrequency: toSafeText(offerReduxSelectors.getPaymentFrequency(primary), DEAL_STRUCTURE_CONTEXT_STRING_CAPS.text)
    };
    if (dealType !== 'Balloon') return paymentDetails;

    const balloonPayment = toFiniteNumber(offerReduxSelectors.getBalloonAmount(primary));
    if (balloonPayment === undefined) return undefined;
    const balloonTerm = toFiniteNumber(offerReduxSelectors.getBalloonTerm(primary));
    return {
        ...paymentDetails,
        balloon: { balloonPayment, ...(balloonTerm && Number.isInteger(balloonTerm) && balloonTerm > 0 && { balloonTerm }) }
    };
};

const buildCashDetails = (primary: StateTree): FinancialDetails['cashDetails'] | undefined => {
    const totalAmountDue = toFiniteNumber(offerReduxSelectors.getCashTotalAmountDueAfterCredits(primary));
    if (totalAmountDue === undefined) return undefined;
    return { totalAmountDue };
};

const toRate = (rate: number, isMoneyFactor: boolean): DealStructureContextRate =>
    isMoneyFactor ? { moneyFactor: rate } : { apr: rate };

const buildRateDetails = (primary: StateTree, offerType: OfferType): FinancialDetails['rateDetails'] => {
    if (offerType === CASH) return { sellRate: {} };

    const isMoneyFactor = offerType === LEASE && offerReduxSelectors.getRateType(primary) === MONEY_FACTOR_RATE_TYPE;
    const isBuyRateMoneyFactor =
        offerType === LEASE && getRateTypeOverrideFromOfferTypeOrSelectedTerm(primary) === MONEY_FACTOR_RATE_TYPE;
    const apr = toFiniteNumber(offerReduxSelectors.getApr(primary, offerType));
    const moneyFactor = isMoneyFactor ? toFiniteNumber(offerReduxSelectors.getMoneyFactor(primary)) : undefined;
    const buyRate = toFiniteNumber(getBuyRateOverrideWithFallback(primary));
    return {
        sellRate: { ...(apr !== undefined && { apr }), ...(moneyFactor !== undefined && { moneyFactor }) },
        ...(buyRate !== undefined && { buyRate: toRate(buyRate, isBuyRateMoneyFactor) })
    };
};

const buildDueInAdvanceDetails = (
    primary: StateTree,
    offerType: OfferType,
    dealType: DealStructureContextDealType
): FinancialDetails['dueInAdvanceDetails'] => {
    const isLease = dealType === 'Lease';
    const netCashDown = isLease ? toFiniteNumber(offerReduxSelectors.getNetCashDown(primary, offerType)) : undefined;
    const dueAtSigning = isLease ? toFiniteNumber(offerReduxSelectors.getDiaDueAtSigning(primary)) : undefined;
    return {
        cashDown: toFiniteNumber(offerReduxSelectors.getDownPayment(primary, offerType)) ?? 0,
        ...(netCashDown !== undefined && { netCashDown }),
        ...(dueAtSigning !== undefined && { dueAtSigning })
    };
};

const buildFeeDetails = (primary: StateTree, offerType: OfferType): FinancialDetails['feeDetails'] => {
    const dealerAndLenderFees = [...getCountedLenderFees(primary), ...getItemizedDealerFees(primary)];
    const fees = [
        ...dealerAndLenderFees.map((fee) => ({ name: fee.dealerFeeName || fee.dealerFeeDesc, amount: fee.dealerFeeAmount })),
        ...getItemizedGovernmentFees(primary).map((fee) => ({ name: fee.name || fee.description, amount: fee.amount }))
    ];
    const { itemCount, items } = toItemList(fees, DEAL_STRUCTURE_CONTEXT_LIST_CAPS.fees, (fee) => toItem(fee.name, fee.amount));
    return {
        feeSummary: { totalFees: toFiniteNumber(offerReduxSelectors.getTotalFee(primary, offerType)) ?? 0 },
        itemCount,
        fees: items
    };
};

const buildTaxDetails = (primary: StateTree, offerType: OfferType): FinancialDetails['taxDetails'] => {
    const { itemCount, items } = toItemList(getFlatOrItemizedTaxes(primary), DEAL_STRUCTURE_CONTEXT_LIST_CAPS.taxes, (tax) =>
        toItem(tax.taxDescription || tax.taxName, tax.taxAmount)
    );
    return {
        taxSummary: { totalTaxes: toFiniteNumber(offerReduxSelectors.getTotalTax(primary, offerType)) ?? 0 },
        itemCount,
        taxes: items
    };
};

const buildIncentiveDetails = (primary: StateTree, offerType: OfferType): FinancialDetails['incentiveDetails'] => {
    const incentives = [
        ...offerReduxSelectors.getAppliedConditionalIncentivesForDisplay(primary, offerType),
        ...offerReduxSelectors.getAppliedNonconditionalIncentivesForDisplay(primary, offerType)
    ];
    const { itemCount, items } = toItemList(incentives, DEAL_STRUCTURE_CONTEXT_LIST_CAPS.incentives, (incentive) =>
        toItem(incentive.name, incentive.amount)
    );
    return {
        incentiveSummary: {
            appliedIncentiveAmount: toFiniteNumber(offerReduxSelectors.getAppliedIncentivesTotalForDisplay(primary, offerType)) ?? 0
        },
        itemCount,
        incentives: items
    };
};

const buildProductDetails = (primary: StateTree): FinancialDetails['productDetails'] => {
    const { itemCount, items } = toItemList(getVppSelectedProducts(primary), DEAL_STRUCTURE_CONTEXT_LIST_CAPS.products, (product) =>
        toItem(product.productName, product.productPrice)
    );
    return {
        productSummary: { totalProducts: toFiniteNumber(getVppSummary(primary).totalProductPrice) ?? 0 },
        itemCount,
        products: items
    };
};

const buildLeaseDetails = (primary: StateTree): FinancialDetails['leaseDetails'] => {
    const annualMiles =
        toNonZeroNumber(offerReduxSelectors.getAnnualMiles(primary)) ??
        toNonZeroNumber(offerReduxSelectors.getInputAnnualMiles(primary));
    const residualValue = toFiniteNumber(offerReduxSelectors.getAdjustedResidualValue(primary));
    const residualPercent = toFiniteNumber(offerReduxSelectors.getAdjustedResidualPercent(primary));
    const capCostTotal = toFiniteNumber(offerReduxSelectors.getCapCostTotal(primary));
    return {
        annualMiles: Math.max(annualMiles ?? 0, 0),
        ...(residualValue !== undefined && { residualValue }),
        ...(residualPercent !== undefined && { residualPercent }),
        ...(capCostTotal !== undefined && { capCostTotal })
    };
};

const buildProgramDetails = (primary: StateTree, offerType: OfferType): FinancialDetails['programDetails'] | undefined => {
    if (offerType === CASH) return undefined;

    const { lenderText } = DEAL_STRUCTURE_CONTEXT_STRING_CAPS;
    const retained = getRetainedProgramDisplay(primary);
    const lenderName = toSafeText(retained?.lenderLegalName || offerReduxSelectors.getLenderLegalName(primary), lenderText);
    if (!lenderName) return undefined;

    const programName = toSafeText(
        retained?.programName || offerReduxSelectors.getSelectedTerm(primary, offerType).lenderProgram?.programName,
        lenderText
    );
    return { lenderName, ...(programName && { programName }) };
};

const buildTradeInVehicles = (primary: StateTree): DealStructureContext['vehicles']['tradeInVehicles'] | undefined => {
    const { text } = DEAL_STRUCTURE_CONTEXT_STRING_CAPS;
    const tradeIns = getIncludedTradeIns(primary)
        .map(({ tradeIn }) => tradeIn)
        .filter((tradeIn) => tradeIn.vin || tradeIn.make || tradeIn.model || tradeIn.value || tradeIn.owed)
        .map((tradeIn): DealStructureContextTradeIn => {
            const trim = toSafeText(tradeIn.trim, text);
            const odometer = toFiniteNumber(tradeIn.mileage);
            return {
                year: tradeIn.year ? toSafeText(tradeIn.year, text) : '',
                make: toSafeText(tradeIn.make, text),
                model: toSafeText(tradeIn.model, text),
                ...(trim && { trim }),
                ...(odometer !== undefined && odometer >= 0 && { odometer }),
                allowance: toFiniteNumber(tradeIn.value) ?? 0,
                payoff: toFiniteNumber(tradeIn.owed) ?? 0
            };
        });
    if (!tradeIns.length) return undefined;

    const { totalAllowance, totalPayoff, netTrade } = getTradeInSummary(primary);
    return {
        tradeInSummary: { totalAllowance, totalPayoff, netTrade },
        itemCount: tradeIns.length,
        tradeIns: tradeIns.slice(0, DEAL_STRUCTURE_CONTEXT_LIST_CAPS.tradeIns)
    };
};

export const buildDealStructureContextFromPrimaryScope = (
    primary: StateTree,
    primaryScopeId: string | undefined
): DealStructureContext | undefined => {
    const scopeId = toSafeText(primaryScopeId, DEAL_STRUCTURE_CONTEXT_STRING_CAPS.scopeId);
    const offerType = offerReduxSelectors.getCurrentOfferType(primary);
    const dealType = getDealType(primary, offerType);
    const sellingVehicle = buildSellingVehicle(primary);
    const sellingPrice = toFiniteNumber(offerReduxSelectors.getOfferPrice(primary));
    if (!scopeId || !dealType || !sellingVehicle || sellingPrice === undefined) return undefined;

    const isCash = dealType === 'Cash';
    const paymentDetails = isCash ? undefined : buildPaymentDetails(primary, offerType, dealType);
    const cashDetails = isCash ? buildCashDetails(primary) : undefined;
    if (!paymentDetails && !cashDetails) return undefined;

    const dealXgId = toSafeText(offerReduxSelectors.getDealXgId(primary), DEAL_STRUCTURE_CONTEXT_STRING_CAPS.dealXgId);
    const summaryDiscount = getSummaryDiscount(primary);
    const discount =
        summaryDiscount === undefined
            ? toFiniteNumber(offerReduxSelectors.getDiscount(primary, offerType))
            : summaryDiscount < 0
            ? undefined
            : toFiniteNumber(summaryDiscount);
    const tradeInVehicles = buildTradeInVehicles(primary);
    const programDetails = buildProgramDetails(primary, offerType);
    return {
        schemaVersion: DEAL_STRUCTURE_CONTEXT_SCHEMA_VERSION,
        scenario: { scopeId, ...(dealXgId && { dealXgId }), capturedAt: new Date().toISOString() },
        dealType,
        vehicles: { sellingVehicle, ...(tradeInVehicles && { tradeInVehicles }) },
        financialDetails: {
            priceDetails: {
                retailPrice: toFiniteNumber(offerReduxSelectors.getRetailPriceOverrideByOfferType(primary, offerType, true)) ?? 0,
                sellingPrice,
                ...(discount !== undefined && { discount })
            },
            ...(paymentDetails && { paymentDetails }),
            ...(cashDetails && { cashDetails }),
            rateDetails: buildRateDetails(primary, offerType),
            dueInAdvanceDetails: buildDueInAdvanceDetails(primary, offerType, dealType),
            feeDetails: buildFeeDetails(primary, offerType),
            taxDetails: buildTaxDetails(primary, offerType),
            incentiveDetails: buildIncentiveDetails(primary, offerType),
            productDetails: buildProductDetails(primary),
            ...(dealType === 'Lease' && { leaseDetails: buildLeaseDetails(primary) }),
            ...(programDetails && { programDetails })
        }
    };
};

export const buildDealStructureContext = (state: StateTree): DealStructureContext | undefined =>
    buildDealStructureContextFromPrimaryScope(getPrimaryScopeState(state), scopedStateSelectors.getPrimaryScopeId(state as any));
